import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Check, RefreshCw, Eye } from "lucide-react";
import type { ImageGenTabProps } from "./types";
import { IMAGE_MODELS } from "./types";
import { useLocale } from "@/hooks/useLocale";

export function ArtDirectionTab(props: ImageGenTabProps) {
  const { t } = useLocale();
  const {
    imageLabel, generationPrompt, setGenerationPrompt, briefLoading, generating,
    briefText, setBriefText, hasCachedBrief, setHasCachedBrief,
    fetchBrief, webSearchEnabled, selectedModel, setSelectedModel,
    seedImageUrl, drawingDataUrl, selectedRefImages,
  } = props;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-primary" /> Art Direction Prompt
        </label>
        <Badge variant="outline" className="text-[9px] mb-1">{imageLabel}</Badge>
        <Textarea
          value={generationPrompt}
          onChange={(e) => setGenerationPrompt(e.target.value)}
          placeholder={t.wb_art_direction_placeholder}
          className="min-h-[100px] text-xs resize-y"
          disabled={briefLoading || generating}
          rows={4}
        />
      </div>

      {/* Brief text editable */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-foreground">AI Visual Brief</label>
          {hasCachedBrief && <Badge variant="outline" className="text-[9px] h-4 gap-1"><Check className="w-2.5 h-2.5" /> Ready</Badge>}
        </div>
        {hasCachedBrief ? (
          <>
            <Textarea
              value={briefText}
              onChange={(e) => setBriefText(e.target.value)}
              className="min-h-[80px] text-xs resize-y font-mono"
              rows={4}
              disabled={generating}
            />
            <Button variant="outline" size="sm" className="gap-1.5 text-xs w-full" onClick={() => fetchBrief(webSearchEnabled)} disabled={briefLoading}>
              <RefreshCw className="w-3 h-3" /> Regenerate Brief
            </Button>
          </>
        ) : (
          <div className="p-3 rounded-lg bg-muted/20 border border-border text-center">
            <p className="text-xs text-muted-foreground">No brief yet. Use the <strong>Research</strong> tab to create one, or write your own below.</p>
            <Textarea
              value={briefText}
              onChange={(e) => { setBriefText(e.target.value); if (e.target.value.trim()) setHasCachedBrief(true); }}
              placeholder={t.wb_art_brief_manual_placeholder}
              className="min-h-[60px] text-xs resize-y mt-2"
              rows={3}
            />
          </div>
        )}
      </div>

      {/* Model selector */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-foreground">Model</label>
        <Select value={selectedModel} onValueChange={setSelectedModel}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {IMAGE_MODELS.map(m => (
              <SelectItem key={m.value} value={m.value} className="text-xs">
                <span className="font-medium">{m.label}</span>
                <span className="text-muted-foreground ml-1">— {m.description}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary */}
      <div className="rounded-lg bg-muted/30 border border-border p-3 space-y-1.5">
        <p className="text-[11px] font-medium text-foreground flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5 text-muted-foreground" /> Generation inputs
        </p>
        <ul className="text-[11px] text-muted-foreground space-y-0.5 pl-5 list-disc">
          {briefText.trim() && <li>Brief ({briefText.length} chars)</li>}
          {generationPrompt.trim() && <li>Art direction prompt</li>}
          {drawingDataUrl && <li>Drawing sketch as seed</li>}
          {seedImageUrl && !drawingDataUrl && <li>Seed image</li>}
          {Array.from(selectedRefImages).filter(u => u !== seedImageUrl).length > 0 && (
            <li>{Array.from(selectedRefImages).filter(u => u !== seedImageUrl).length} reference(s)</li>
          )}
          {!seedImageUrl && !drawingDataUrl && selectedRefImages.size === 0 && <li>No reference images (text-only)</li>}
        </ul>
      </div>
    </div>
  );
}
